import { Component, OnInit, Renderer2 } from '@angular/core';
import { FrontEndService } from "../../services/front-end.service";
import { UserManagementService } from "../../services/user-management.service";
import { WorkSpaceService } from "../../services/work-space.service";
import tableDragger from 'table-dragger';
import appConstants from "../../app.constants";
import { DataService } from "../../services/data.service";
import { TranslateService } from "@ngx-translate/core";
import * as _ from "lodash";

@Component({
    selector: 'app-modify-columns',
    templateUrl: './modify-columns.component.html',
    styleUrls: ['./modify-columns.component.scss']
})
export class ModifyColumnsComponent implements OnInit {

    loading = false;
    users = [];
    newColumn = {
        key_name: "",
        display_name: "",
        required: false,
        col_is_hidden: false,
        col_width: 50,
        attr_kwargs: {
            "default": null,
            "null": true,
            "blank": true
        },
        attr_field: null,
        filled_by: null,
        new: true
    };

    choiceObject = {
        default: null,
        max_length: 255,
        blank: true,
        null: true,
        choices: {},
        newChoices: []
    };

    fieldTypeOptions = {
        placeholder: 'Select Field Type'
    };

    fieldTypes = [
        {
            id: 'TextField',
            text: 'Text Field'
        },
        {
            id: 'CharField',
            text: 'Choices'
        },
        {
            id: 'DateField',
            text: 'Date'
        }
    ];

    fieldTypeName = {
        'TextField': 'Text Field',
        'IntegerField': 'Number',
        'ForeignKey': 'Choices',
        'CharField': 'Choices',
        'DateField': 'Date'
    };

    filledByOptions = {
        placeholder: 'Select Filled By'
    };

    filledByTypes = [
        {
            id: 'admin',
            text: 'Admin'
        },
        {
            id: 'analyst',
            text: 'Analyst'
        },
        {
            id: 'reviewer',
            text: 'Reviewer'
        },
    ];

    fixedColumns = [];
    fieldOrderColumns = [];
    originalFields = [];

    constructor(
        public frontEndService: FrontEndService,
        private userManagementService: UserManagementService,
        private workSpaceService: WorkSpaceService,
        private renderer: Renderer2,
        private dataService: DataService,
        private translateService: TranslateService
    ) { }

    ngOnInit() {
        this.getUsers();
    }

    /**
     * Get users from backend
     */
    getUsers() {
        this.loading = true;
        this.userManagementService.getUsers((users) => {
            this.loading = false;
            this.users = users;
            this.getFields();
        });
    }

    /**
     * Get fields from backend
     */
    getFields() {
        this.loading = true;
        this.workSpaceService.getFields(this.users, false, (response) => {
            this.loading = false;
            if (response.status) {
                this.setFieldOrderColumns(response.data);
                this.setDragging();
            }
        });
    }

    setFieldOrderColumns(columns) {
        this.fixedColumns = columns.filter((column) => {
            return !this.shouldShowColumnToReorder(column);
        });

        this.fieldOrderColumns = columns.filter((column) => {
            return this.shouldShowColumnToReorder(column);
        });
        this.originalFields = JSON.parse(JSON.stringify(this.fieldOrderColumns));
    }

    /**
     * Validate the fields and then show alert before hitting the api
     */
    modifyFields(fieldsForm) {
        if (!fieldsForm.form.valid) {
            this.frontEndService.showAlert("Error", "please_fill_all_required_fields", true);
        } else if(this.checkIfFieldsChanged()) {
            this.frontEndService.showAlert("Error", "no_changes_to_be_saved", true);
        } else {
            this.frontEndService.confirmAlert("confirm", "are_you_sure_you_want_to_update_the_fields", "warning", (result) => {
                if (result.value) {
                    const allColumns = JSON.parse(JSON.stringify(this.fixedColumns.concat(this.fieldOrderColumns)));
                    allColumns.forEach((column) => {
                        if (column.new && column.attr_field === 'CharField') {
                            column.attr_kwargs.choices = {};
                            column.attr_kwargs.newChoices.forEach((choice, choiceIndex) => {
                                if (choiceIndex === 0) {
                                    column.attr_kwargs.default = choice.value;
                                }
                                column.attr_kwargs.choices[choice.value] = choice.value;
                            });
                            delete column.attr_kwargs.newChoices;

                            if (column.key_name === 'status') {
                                delete column.attr_kwargs.null;
                                delete column.attr_kwargs.blank;
                            }
                        }
                    });

                    this.loading = true;
                    this.workSpaceService.modifyFields(allColumns, (response) => {
                        if (response.status) {
                            this.frontEndService.addToast("Success", response.message, "success");
                            this.fieldOrderColumns.forEach((column) => {
                                delete column.new;
                            });
                            this.originalFields = JSON.parse(JSON.stringify(this.fieldOrderColumns));
                            this.userManagementService.setUserSettings({}, +this.dataService.currentUser['id'], (settingResponse) => {
                                this.loading = false;
                            });
                        } else {
                            this.loading = false;
                        }
                    });
                }
            });
        }
    }

    /**
     * Add new empty column by making a copy of the new column to keep new column intact
     */
    addColumn() {
        this.newColumn['key_name'] = this.getNewKeyName();
        this.fieldOrderColumns.push(JSON.parse(JSON.stringify(this.newColumn)));

        setTimeout(() => {
            const element = this.renderer.selectRootElement(`#display_name_${this.newColumn['key_name']}`);
            element.focus();
            this.setDragging();
        }, 10);
    }

    /**
     * Remove the column at the index
     * @param index {number} - Index of the column to be removed
     */
    removeColumn(index: number) {
        if (!this.isNewColumn(this.fieldOrderColumns[index])) {
            this.frontEndService.confirmAlert("confirm", "are_you_sure_you_want_to_delete_field", "warning", (result) => {
                if (result.value) {
                    this.fieldOrderColumns.splice(index, 1);
                }
            });
        } else {
            this.fieldOrderColumns.splice(index, 1);
        }

        this.setDragging();
    }

    /**
     * Check if its a new column or already created column
     * @param column
     */
    isNewColumn(column) {
        return column.new;
    }

    selectFieldType(key, event, index) {
        this.fieldOrderColumns[index][key] = event.value;
        if (key === 'attr_field' && event.value === 'CharField') {
            this.fieldOrderColumns[index].attr_kwargs = JSON.parse(JSON.stringify(this.choiceObject));
        }
    }

    /**
     * Set row dragging on the table to adjust field positions
     */
    setDragging() {
        setTimeout(() => {
            const el = document.getElementById('table');
            const dragger = tableDragger(el, {
                mode: 'row',
                dragHandler: '.handle',
                onlyBody: true,
                // animation: 300
            });

            dragger.on('drop', (from, to) => {
                // Update position in allColumns array
                this.fieldOrderColumns = this.dataService.dragArrayElement(this.fieldOrderColumns, from, to);
            });
        }, 100);
    }

    shouldShowColumnToReorder(column) {
        return column.key_name !== 'id' && column.key_name !== 'patent_number' && column.key_name !== 'hyperlink' && column.key_name !== 'actions';
    }

    showDeleteButton(key) {
        return !appConstants.reservedKeys.includes(key);
    }

    showField(key) {
        return !appConstants.hiddenFields.includes(key);
    }

    /**
     * Checks if key name exists and returns new unique keyName accordingly
     * @param keyIndex
     */
    getNewKeyName(keyIndex = 1): string {
        const keyName = `custom_field_${keyIndex}`;
        let foundDuplicate = false;
        // Check for key name in both original fields and modified fields
        this.originalFields.concat(this.fieldOrderColumns).forEach((field) => {
            if (field.key_name === keyName) {
                foundDuplicate = true;
            }
        });
        if (foundDuplicate) {
            return this.getNewKeyName(++keyIndex);
        } else {
            return keyName;
        }
    }

    /**
     * Return true if there are no changes in fields
     */
    checkIfFieldsChanged() {
        return _.isEqual(this.fieldOrderColumns, this.originalFields);
    }
}
